import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createPanel } from '../../Repositories';

const panelReq = createPanel();

/**
 * this function(query) is to get panels
 * @function usePanelsGetQuery
 * @param {Object} reqParams params pass along with the api call
 * @param {Object} options to be passed as query handling
 * @returns api response
 */
export function usePanelsGetQuery(reqParams, options) {
  return useQuery(['panelGetQuery', reqParams], () => panelReq.getPanelsRound(reqParams), {
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    ...options,
  });
}

/**
 * this function(query) is to get panels based on application details for suggested panels
 * @function usePanelsApplicationGetQuery
 * @param {Object} reqParams params pass along with the api call
 * @param {Object} options to be passed as query handling
 * @returns api response
 */
export function usePanelsApplicationGetQuery(reqParams, options = {}) {
  const { enabled = true, ...queryOptions } = options;
  const canFetchPanels =
    Number(reqParams?.duration) > 0 &&
    Number.isFinite(Number(reqParams?.startTime)) &&
    Number(reqParams?.startTime) > 0;

  return useQuery(
    ['panelApplicationGetQuery', reqParams],
    () => panelReq.getPanelsApplication(reqParams),
    {
      enabled: canFetchPanels && enabled,
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      ...queryOptions,
    }
  );
}

/**
 * this function(query) is to post panel
 * @function usePanelPostQuery
 * @returns api response
 */
export function usePanelPostQuery() {
  const queryClient = useQueryClient();
  return useMutation(['panelPostQuery'], (args) => panelReq.postPanel(args), {
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries(['panelGetQuery']);
    },
  });
}

/**
 * this function(query) is to post panel resource
 * @function usePostQuery
 * @returns api response
 */
export function usePanelResourcePostQuery() {
  const queryClient = useQueryClient();
  return useMutation(['panelResourcePostQuery'], (args) => panelReq.postResourcePanel(args), {
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries(['panelResourceGetQuery']);
    },
  });
}

/**
 * this function(query) is to update panel by Id
 * @function usePanelPutQuery
 * @returns api response
 */
export function usePanelPutQuery() {
  const queryClient = useQueryClient();
  return useMutation(['panelPutQuery'], (args) => panelReq.putPanel(args), {
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries(['panelGetQuery']);
    },
  });
}

/**
 * this function(query) is to update panel resource by Id
 * @function usePanelResourcePutQuery
 * @returns api response
 */
export function usePanelResourcePutQuery() {
  const queryClient = useQueryClient();
  return useMutation(['panelPutQuery'], (args) => panelReq.putPanelResource(args), {
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries(['panelResourceGetQuery']);
    },
  });
}

/**
 * this function(query) is to get all panel resources
 * @function usePanelResourceGetQuery
 * @returns api response
 */
export function usePanelResourceGetQuery(reqParams, options) {
  return useQuery(
    ['panelResourceGetQuery', reqParams],
    () => panelReq.getPanelResource(reqParams),
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      ...options,
    }
  );
}

/**
 * this function(query) is to deletes panel resource by Id
 * @function usePanelResourceDeleteQueryById
 * @returns api response
 */
export function usePanelResourceDeleteQueryById() {
  const queryClient = useQueryClient();
  return useMutation(
    ['panelResourceDeleteQueryById'],
    (args) => panelReq.deletePanelResourceById(args),
    {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(['panelResourceGetQuery']);
      },
    }
  );
}

export function usePanelsByIdsQuery(idsStr, options) {
  return useQuery(['panelGetByIdsQuery', idsStr], () => panelReq.getPanelsByIds(idsStr), {
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    ...options,
  });
}
